import { defineDocument } from '../../schema';
export const featureTwitchDocument = defineDocument({
    id: 'feature:twitch',
    kind: 'feature',
    slug: 'twitch',
    route: '/discover/features/twitch',
    seo: {
        title: 'Twitch Integration',
        description: 'Connect your Twitch account to Airhead Games for faster sign-in, Poppy in your chat, moderation sync, Going Live promotion, and community clips.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Feature',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Twitch Integration',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Connect your Twitch account to Airhead Games for faster sign-in, Poppy in your chat, moderation sync, Going Live promotion, and community clips.',
            },
        ],
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'twitch-permissions',
            title: [{ type: 'text', text: 'Twitch Permissions' }],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Linking your Twitch account lets Airhead Games work with your channel. Twitch asks for your permission before Airhead Games can read or post anything, and Airhead Games asks only when you turn on a feature that needs it.',
                        },
                    ],
                },
                {
                    type: 'table',
                    columns: [
                        [{ type: 'text', text: 'Feature' }],
                        [{ type: 'text', text: 'What Twitch asks you to allow' }],
                        [{ type: 'text', text: 'When Twitch asks' }],
                    ],
                    rows: [
                        [
                            [
                                {
                                    type: 'strong',
                                    children: [
                                        { type: 'text', text: 'Connect Your Twitch Account' },
                                    ],
                                },
                            ],
                            [{ type: 'text', text: 'Read your email address' }],
                            [{ type: 'text', text: 'When you sign in or link your account' }],
                        ],
                        [
                            [
                                {
                                    type: 'strong',
                                    children: [{ type: 'text', text: 'Poppy in Twitch Chat' }],
                                },
                            ],
                            [{ type: 'text', text: 'Let Poppy chat in your channel' }],
                            [
                                {
                                    type: 'text',
                                    text: 'The first time you turn on Auto-Post Room Join Link or Respond to Commands',
                                },
                            ],
                        ],
                        [
                            [
                                {
                                    type: 'strong',
                                    children: [
                                        { type: 'text', text: 'Disallow Blocked & Banned' },
                                    ],
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Read your blocked users and your channel bans',
                                },
                            ],
                            [{ type: 'text', text: 'The first time you enable the feature' }],
                        ],
                        [
                            [
                                {
                                    type: 'strong',
                                    children: [{ type: 'text', text: 'Blocked on Twitch' }],
                                },
                            ],
                            [{ type: 'text', text: "Read your channel's blocked terms" }],
                            [{ type: 'text', text: 'The first time you enable the feature' }],
                        ],
                        [
                            [
                                {
                                    type: 'strong',
                                    children: [{ type: 'text', text: 'Going Live and Clips' }],
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Nothing. These use public Twitch info.',
                                },
                            ],
                            [{ type: 'text', text: 'Never' }],
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'These permissions map to Twitch scopes such as user:read:email, channel:bot, user:read:blocked_users, moderation:read, and moderator:read:blocked_terms. Twitch explains each one in its ',
                        },
                        {
                            type: 'link',
                            href: 'https://dev.twitch.tv/docs/authentication/scopes/',
                            children: [{ type: 'text', text: 'scope reference' }],
                        },
                        { type: 'text', text: ' documentation.' },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Airhead Games is mindful to never ask for more permissions than necessary to deliver the features you enable on our platform.',
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'login-with-twitch',
                    title: [{ type: 'text', text: 'Connect Your Twitch Account' }],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'You can sign in with Twitch, or link Twitch to an email account later. See ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/login#twitch-login',
                                    children: [{ type: 'text', text: 'Login Options' }],
                                },
                                { type: 'text', text: ' for the steps.' },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Connecting only shares your email address and Twitch ID.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'poppy-in-twitch-chat',
                    title: [{ type: 'text', text: 'Poppy in Twitch Chat' }],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Poppy can post messages in your chat from the ',
                                },
                                {
                                    type: 'link',
                                    href: 'https://www.twitch.tv/poppy_airhead_games',
                                    children: [{ type: 'text', text: 'poppy_airhead_games' }],
                                },
                                {
                                    type: 'text',
                                    text: ' account, and Twitch shows a Chat Bot badge next to each message. We regularly recheck that Poppy has permission to continue posting without you having to take any action. See ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/moderator-tools#poppy-in-twitch-chat',
                                    children: [{ type: 'text', text: 'Moderator Tools' }],
                                },
                                {
                                    type: 'text',
                                    text: ' for the full list of chat capabilities.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'block-and-banned-sync',
                    title: [{ type: 'text', text: 'Disallow Blocked & Banned' }],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Disallow Blocked & Banned copies the users you blocked on Twitch and the users banned from your channel, then keeps them out of your rooms. The lists refresh each time you sign in. Players must sign in with Twitch for the check to work. See ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/moderator-tools#player-access__disallow-blocked-banned',
                                    children: [{ type: 'text', text: 'Moderator Tools' }],
                                },
                                { type: 'text', text: ' for setup.' },
                            ],
                        },
                    ],
                },
                {
                    id: 'blocked-terms-sync',
                    title: [{ type: 'text', text: 'Blocked on Twitch' }],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "Blocked on Twitch adds your channel's blocked terms to your profanity filter. Twitch only shares terms that are not marked private, so private terms stay on Twitch. The terms refresh each time you sign in. See ",
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/moderator-tools#censorship__blocked-on-twitch',
                                    children: [{ type: 'text', text: 'Moderator Tools' }],
                                },
                                { type: 'text', text: ' for setup.' },
                            ],
                        },
                    ],
                },
                {
                    id: 'twitch-permissions__manage',
                    title: [{ type: 'text', text: 'Revoke Our Access' }],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'You can always disable the features you no longer want to use, but can also fully disconnect Airhead Games from your ',
                                },
                                {
                                    type: 'link',
                                    href: 'https://www.twitch.tv/settings/connections',
                                    children: [{ type: 'text', text: 'Twitch Connections' }],
                                },
                                {
                                    type: 'text',
                                    text: '.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'If Poppy in Twitch Chat features are enabled, then "Poppy from Airhead Games" will appear as its own entry next to the regular "Airhead Games" connection. If you revoke access, the features requiring the connection will stop working. Re-enabling a feature will request your authorization to reconnect Airhead Games to Twitch.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'going-live',
            title: [
                {
                    type: 'text',
                    text: 'Going Live',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Going Live detection helps more viewers discover your stream and jump into your games. When all three conditions are met:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Your Twitch account is linked to your Airhead Games profile',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'You are streaming live on Twitch',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'You open a new game room',
                            },
                        ],
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featureLivePill',
                    imageAlt: 'Airhead Games landing page showing LIVE badge on streamer profile.',
                    caption: [
                        {
                            type: 'text',
                            text: 'LIVE streamers appear across the website with a clickthrough to your channel.',
                        },
                    ],
                    maxWidth: 1200,
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'You will be displayed as LIVE across all landing pages of the Airhead Games website and an announcement will automatically post to the ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: '#going-live',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' channel in the ',
                        },
                        {
                            type: 'link',
                            href: 'https://discord.gg/rfFTVAqHwH',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Discord',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: " community. Opening a new game room also triggers a message in the game's specific Discord channel. These features are designed to create opportunities for new audiences and the Airhead Games community to discover your channel and join your games in real time.",
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featureLiveDiscord',
                    imageAlt: 'Discord #going-live channel announcement with Twitch stream link.',
                    caption: [
                        {
                            type: 'text',
                            text: 'Auto-posted Discord announcements.',
                        },
                    ],
                    maxWidth: 400,
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Airhead Games helps promote streamers who play our games by sharing Twitch clips of community gameplay across our website and social channels. Browse the ',
                        },
                        {
                            type: 'link',
                            href: '/discover/clips',
                            children: [
                                {
                                    type: 'text',
                                    text: 'clips library',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' and filter by game or streamer to find your favorites.',
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featureClipsLibrary',
                    imageAlt: 'Clips discover page showing grid of Twitch gameplay clips.',
                    caption: [
                        {
                            type: 'text',
                            text: 'Community clips showcased on the Clips discover page.',
                        },
                    ],
                    maxWidth: 800,
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'We post clips to ',
                        },
                        {
                            type: 'link',
                            href: 'https://www.youtube.com/@airhead-games',
                            children: [
                                {
                                    type: 'text',
                                    text: 'YouTube',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ', ',
                        },
                        {
                            type: 'link',
                            href: 'https://www.instagram.com/poppy_airhead_games/',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Instagram',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ', and ',
                        },
                        {
                            type: 'link',
                            href: 'https://www.tiktok.com/@poppy_airhead_games',
                            children: [
                                {
                                    type: 'text',
                                    text: 'TikTok',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' with the goal of helping more people discover both Airhead Games and the amazing streamers in our community.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Want help spreading awareness about your channel?',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Save VODs to your channel. We watch every stream and clip the moments we love most.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Create your own clips and drop them in ',
                            },
                            {
                                type: 'link',
                                href: 'https://discord.gg/rfFTVAqHwH',
                                children: [
                                    {
                                        type: 'text',
                                        text: "Poppy's Pals Discord",
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: '.',
                            },
                        ],
                    ],
                },
            ],
        },
    ],
});
//# sourceMappingURL=twitch.js.map